import type { ModelEntity, WallSegment } from './types';

const EPSILON = 1e-7;

function uniqueSorted(values: number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const result: number[] = [];
  for (const value of sorted) {
    if (result.length === 0 || Math.abs(value - result[result.length - 1]) > EPSILON) result.push(value);
  }
  return result;
}

function unionIntervals(intervals: Array<[number, number]>): Array<[number, number]> {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const result: Array<[number, number]> = [];

  for (const interval of sorted) {
    const last = result[result.length - 1];
    if (!last || interval[0] > last[1] + EPSILON) result.push([...interval] as [number, number]);
    else last[1] = Math.max(last[1], interval[1]);
  }
  return result;
}

/**
 * Partitions a wall into non-overlapping rectangular solids around rectangular openings.
 * Coordinates are local to the wall: X along length and Y from wall base.
 */
export function computeWallSegments(wall: ModelEntity): WallSegment[] {
  const length = Math.max(EPSILON, wall.dimensions.width);
  const height = Math.max(EPSILON, wall.dimensions.height);
  const openings = (wall.openings ?? [])
    .map((opening) => ({
      ...opening,
      left: Math.max(-length / 2, opening.center - opening.width / 2),
      right: Math.min(length / 2, opening.center + opening.width / 2),
      bottom: Math.max(0, opening.sill),
      top: Math.min(height, opening.sill + opening.height),
    }))
    .filter((opening) => opening.right - opening.left > EPSILON && opening.top - opening.bottom > EPSILON);

  if (openings.length === 0) return [{ x: 0, y: height / 2, width: length, height }];

  const xBoundaries = uniqueSorted([
    -length / 2,
    length / 2,
    ...openings.flatMap((opening) => [opening.left, opening.right]),
  ]);

  const segments: WallSegment[] = [];
  for (let i = 0; i < xBoundaries.length - 1; i += 1) {
    const x0 = xBoundaries[i];
    const x1 = xBoundaries[i + 1];
    if (x1 - x0 <= EPSILON) continue;
    const midpoint = (x0 + x1) / 2;

    const blocked = unionIntervals(
      openings
        .filter((opening) => midpoint > opening.left - EPSILON && midpoint < opening.right + EPSILON)
        .map((opening) => [opening.bottom, opening.top] as [number, number]),
    );

    let cursor = 0;
    for (const [bottom, top] of blocked) {
      if (bottom > cursor + EPSILON) {
        segments.push({ x: midpoint, y: (cursor + bottom) / 2, width: x1 - x0, height: bottom - cursor });
      }
      cursor = Math.max(cursor, top);
    }
    if (cursor < height - EPSILON) {
      segments.push({ x: midpoint, y: (cursor + height) / 2, width: x1 - x0, height: height - cursor });
    }
  }

  return segments;
}
